import { useState } from "react";
import * as XLSX from "xlsx";
import "bootstrap/dist/css/bootstrap.min.css";

export default function Home() {
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState(null);
  const [stats, setStats] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const fileContent = event.target.result;
      setKeywords(fileContent.trim());
    };
    reader.readAsText(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setClusters(null);

    const keywordsArray = keywords.split("\n").map((kw) => kw.trim()).filter(Boolean);

    if (keywordsArray.length === 0) {
      alert("Please enter some keywords or upload a file.");
      setLoading(false);
      return;
    }

    // Simulate clustering process
    const simulateClustering = () => {
      const clusters = {};
      keywordsArray.forEach((keyword, index) => {
        const group = index % 5; // Mock grouping logic
        if (!clusters[group]) clusters[group] = [];
        clusters[group].push(keyword);
      });
      return clusters;
    };

    const calculateStats = (clusters) => {
      const clusterSizes = Object.values(clusters).map((group) => group.length);
      const totalKeywords = keywordsArray.length;
      const clusteredKeywords = clusterSizes.reduce((a, b) => a + b, 0);
      const totalClusters = clusterSizes.length;
      const avgClusterSize = (clusteredKeywords / totalClusters).toFixed(2);
      const medianClusterSize = (
        clusterSizes.sort((a, b) => a - b)[Math.floor(clusterSizes.length / 2)] || 0
      ).toFixed(2);

      return {
        totalKeywords,
        clusteredKeywords,
        totalClusters,
        avgClusterSize,
        medianClusterSize,
      };
    };

    setTimeout(() => {
      const clusterResult = simulateClustering();
      setClusters(clusterResult);
      setStats(calculateStats(clusterResult));
      setLoading(false);
    }, 2000); // Simulate processing time
  };

  const downloadCSV = () => {
    if (!clusters) return;

    const rows = [];
    Object.entries(clusters).forEach(([group, keywords]) => {
      keywords.forEach((keyword) => {
        rows.push({ Group: `Group ${group}`, Keyword: keyword });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clusters");
    XLSX.writeFile(workbook, "clustered_keywords.xlsx");
  };

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
            

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <textarea
                    className="form-control"
                    rows="5"
                    placeholder="Enter keywords (one per line)"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                  ></textarea>
                </div>

                <div className="mb-3">
                  <label
                    htmlFor="file-upload"
                    className="form-label text-primary fw-bold"
                  >
                    Upload a text file with keywords:
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".txt"
                    className="form-control"
                    onChange={handleFileUpload}
                  />
                </div>

                <div className="d-grid">
                  <button
                    type="submit"
                    className={`btn btn-primary ${loading ? "disabled" : ""}`}
                    disabled={loading}
                  >
                    {loading ? "Processing..." : "Create Clusters"}
                  </button>
                </div>
              </form>

              {loading && (
                <div className="text-center mt-4">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p className="text-primary mt-3 fw-bold">Processing...</p>
                </div>
              )}

              {stats && clusters && (
                <div className="mt-5">
                  <h2 className="text-primary mb-4 text-center">Statistics</h2>
                  <div className="row">
                    <div className="col-md-2 col-lg-2 mb-3 ms-5">
                      <div className="card border-primary">
                        <div className="card-body text-center">
                          <h3 className="text-primary">{stats.totalKeywords}</h3>
                          <p>Total Keywords</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2 col-lg-2 mb-3">
                      <div className="card border-primary">
                        <div className="card-body text-center">
                          <h3 className="text-primary">{stats.clusteredKeywords}</h3>
                          <p>Clustered Keywords</p>
                        </div>
                      </div>
                    </div>
                   <div className="col-md-2 col-lg-2 mb-3">
                      <div className="card border-primary">
                        <div className="card-body text-center">
                          <h3 className="text-primary">{stats.totalClusters}</h3>
                          <p>Number of Clusters</p>
                        </div>
                      </div>
                    </div>
                   <div className="col-md-2 col-lg-2 mb-3">
                      <div className="card border-primary">
                        <div className="card-body text-center">
                          <h3 className="text-primary">{stats.avgClusterSize}</h3>
                          <p>Average Cluster Size</p>
                        </div>
                      </div>
                    </div>
                   <div className="col-md-2 col-lg-2 mb-3">
                      <div className="card border-primary">
                        <div className="card-body text-center">
                          <h3 className="text-primary">{stats.medianClusterSize}</h3>
                          <p>Median Cluster Size</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {clusters && (
                <div className="mt-5">
                  <h2 className="text-primary mb-4">Clustered Keywords</h2>
                  <div className="row">
                    {Object.entries(clusters).map(([group, keywords]) => (
                      <div className="col-md-6 col-lg-4 mb-3" key={group}>
                        <div className="card border-primary">
                          <div className="card-body">
                            <h5 className="text-primary fw-bold">Group {group}</h5>
                            <ul className="list-unstyled">
                              {keywords.map((keyword, index) => (
                                <li key={index}>- {keyword}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={downloadCSV}
                    className="btn btn-success w-100 mt-4"
                  >
                    Download CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
